import {configureStore, ThunkAction, Action, combineReducers, getDefaultMiddleware, Middleware} from '@reduxjs/toolkit';
import {
    persistStore,
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER
} from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';

import fcltDeptS_static from 'features/fcltDeptSlice_static';
import counterReducer from 'features/counter/counterSlice';
import searchPgUserInputs from 'features/content/search_page/UserInputsSlice';
import searchBlockS from 'features/content/search_page/components/SearchBlock/SearchBlockSlice';
import {
    rsrFilterS, 
    pubFilterS,
    gsPubFilterS,
    smtPubFilterS,
    altTrendingFilterS
} from '../features/content/search_page/components/FilterBlock/';
import creationFilterS from '../features/content/search_page/components/FilterBlock/CreationFilterBlock/CreationFilterBlockSlice';
import {
    rsrTabS, 
    pubTabS,
    gsPubTabS,
    smtPubTabS,
    altTrendingTabS
} from '../features/content/search_page/components/TabBlock/';
import creationTabS from '../features/content/search_page/components/TabBlock/CreationTab/creationTabSlice'
import authenticationS from "../features/authentication/authenticationSlice";
import authenticationErrorReducer from "../features/authentication/authenticationErrorSlice";
import generalPanelS from "../features/content/setting_page/GeneralPanel/GeneralPanelSlice";
import creationPanelReducer from "../features/content/setting_page/CreationPanel/creationPanelSlice";
import SDGPanelS from "../features/content/setting_page/SDGPanel/SDGPanelSlice";
import loginControllerReducer from '../features/header/loginController/loginControllerSlice';
import profilePgS from '../features/content/profile_page/profilePgSlice';
import profilePg_scopusTab_S from 'features/content/profile_page/tabs/ScopusTab/scopusTabSlice';
import profilePg_gsPubTab_S from 'features/content/profile_page/tabs/GsPubTab/gsPubTabSlice';
import profilePg_smtPubTab_S from 'features/content/profile_page/tabs/SmtPubTab/smtPubTabSlice';
import profilePg_creationTab_S from '../features/content/profile_page/tabs/CreationTab/creationTabSlice';
import { GoogleAnalyticsSearchEvent, SearchTab } from 'utils/types';

const rootReducer = combineReducers({
    counter: counterReducer,
    fcltDept_static: fcltDeptS_static.reducer,
    searchPgUserInputs: searchPgUserInputs.reducer,
    searchBlock: searchBlockS.reducer,
    rsrFilter: rsrFilterS.reducer,
    pubFilter: pubFilterS.reducer,
    gsPubFilter: gsPubFilterS.reducer,
    smtPubFilter: smtPubFilterS.reducer,
    creationFilter: creationFilterS.reducer,
    altTrendingFilter: altTrendingFilterS.reducer,
    rsrTab: rsrTabS.reducer,
    pubTab: pubTabS.reducer,
    gsPubTab: gsPubTabS.reducer,
    smtPubTab: smtPubTabS.reducer,
    creationTab: creationTabS.reducer,
    altTrendingTab: altTrendingTabS.reducer,
    authentication: authenticationS.reducer,
    authenticationError: authenticationErrorReducer,
    generalPanel: generalPanelS.reducer,
    creationPanel: creationPanelReducer,
    loginController: loginControllerReducer,
    profilePg: profilePgS.reducer,
    profilePg_scopusTab: profilePg_scopusTab_S.reducer,
    profilePg_gsPubTab: profilePg_gsPubTab_S.reducer,
    profilePg_smtPubTab: profilePg_smtPubTab_S.reducer,
    profilePg_creationTab: profilePg_creationTab_S.reducer,
    SDGPanel: SDGPanelS.reducer
});

const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['authentication'], // reducer you want to persist
    blacklist: [],  // reducer you don't want to persist
}

const persistedReducer = persistReducer(persistConfig, rootReducer);

export type RootState = ReturnType<typeof rootReducer>;

/**
 * Logs all actions and states after they are dispatched.
 */
// const loggerMiddleware: Middleware<{}, RootState> = (store) => (next) => (action) => {
//   if (action.type.includes('setSearchInput') && !action.type.includes('_internal')) {
//     console.group(action.type);
//     console.info("dispatching", action);
//     const result = next(action);
//     console.log("next state", store.getState());
//     console.groupEnd();
//     return result
//   } else {
//     const result = next(action)
//     return result
//   }
// };

// https://www.analyticsmania.com/post/track-site-search-with-google-tag-manager-and-google-analytics/
const googleAnalyticsMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action) => {
    const searchEventActionTypes = [
      "rsrTab/initList/pending",
      "pubTab/initList/pending",
      "altTrendingTab/initList/pending",
      "searchPage/creationTab/initList/pending",
    ];
    if (searchEventActionTypes.includes(action.type)) {
      const result = next(action);
      const {
        searchInput,
        keywordType,
        facultyID,
        departmentID,
        searchTab,
        rsrSpecific,
      } = store.getState().searchPgUserInputs;

      const { fclts, depts } = store.getState().fcltDept_static;

      const searchEvent: GoogleAnalyticsSearchEvent = {
        event: "search",
        ree_search_value:
          searchTab === SearchTab.TRENDING ? "n/a" : searchInput || "",
        ree_keyword_type:
          searchTab === SearchTab.TRENDING
            ? "n/a"
            : keywordType === "search"
            ? "keyword"
            : "researcher",
        ree_faculty: (facultyID !== -1 && fclts?.[facultyID]?.name) || "all",
        ree_department:
          (departmentID !== -1 && depts?.[departmentID]?.name) || "all",
        ree_tab: searchTab,
        ree_accepting_grad_students:
          searchTab !== SearchTab.RESEARCHERS
            ? "n/a"
            : rsrSpecific?.acceptingGraduateStudentChecked
            ? "yes"
            : "no",
        ree_C3I:
          searchTab !== SearchTab.RESEARCHERS
            ? "n/a"
            : rsrSpecific?.C3IChecked
            ? "yes"
            : "no",
        ree_sdg: searchTab !== SearchTab.RESEARCHERS ? "n/a" : rsrSpecific.SDGs,
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(searchEvent);

      return result;
    } else {
      const result = next(action);
      return result;
    }
  };

export const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware({
        serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
        }
    }).concat(googleAnalyticsMiddleware)
});

export const persistor = persistStore(store);

export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    Action<string>
>;
